<template>
  <el-dialog 
    :visible="true" 
    width="85%" 
    top="5vh"
    destroy-on-close 
    @close="$emit('close')"
  >
    <div slot="title" class="popup-header">
      <span class="header-title">{{ camera.name }}</span>
      <div class="header-tools">
        <span class="tip-text"><i class="el-icon-mouse"></i> 支援滾輪縮放與拖曳</span>
        <el-button-group>
          <el-button size="small" icon="el-icon-zoom-in" @click="changeZoom(0.5)">放大</el-button>
          <el-button size="small" icon="el-icon-c-scale-to-original" @click="resetZoom">1:1</el-button>
          <el-button size="small" icon="el-icon-zoom-out" @click="changeZoom(-0.5)">縮小</el-button>
        </el-button-group>
      </div>
    </div>

    <div class="main-container">
      <el-row :gutter="20">
        
        <el-col :span="12">
          <el-card shadow="hover" :body-style="{ padding: '0px' }">
            <div slot="header" class="card-header">
              <span class="card-title">對照影像</span>
              <div class="card-actions">
                <span class="time-badge">{{ camera.compare || '無時間紀錄' }}</span>
                <el-button size="small" :type="showCompareBox ? 'danger' : 'default'" plain @click="showCompareBox = !showCompareBox">
                  <i class="el-icon-aim"></i> {{ showCompareBox ? '隱藏標示' : '顯示標示' }}
                </el-button>
              </div>
            </div>
            
            <div class="img-wrapper" ref="leftContainerRef" @wheel.prevent="handleWheel" @mousedown="startDrag">
              <div class="img-inner" :style="panZoomStyle">
                <template v-if="secureSpriteUrl">
                  <img :src="secureSpriteUrl" class="sprite-img" :style="{ transform: `translate(0%, ${showCompareBox ? '-50%' : '0%'})` }" draggable="false" />
                </template>
                <template v-else>
                  <img :src="compareImageUri" class="twin-img" draggable="false" />
                  <div v-if="showCompareBox && camera.boxPosition && !hasOverlayData" :style="getBoxStyle(camera.boxPosition)"></div>
                </template>

                <svg v-if="showCompareBox && hasOverlayData" class="overlay-svg" viewBox="0 0 1920 1080" preserveAspectRatio="none">
                  <rect v-for="(box, i) in camera.bboxes" :key="'l-box-'+i" 
                        :x="box[0]" :y="box[1]" :width="box[2]-box[0]" :height="box[3]-box[1]" 
                        class="event-bbox" />
                  <polygon v-for="(poly, i) in camera.polygons" :key="'l-poly-'+i" 
                           :points="formatPolygonPoints(poly)" 
                           class="event-polygon" />
                </svg>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :span="12">
          <el-card shadow="hover" :body-style="{ padding: '0px' }">
            <div slot="header" class="card-header">
              <span class="card-title">異常影像</span>
              <div class="card-actions">
                <span class="time-badge">{{ camera.time || '無時間紀錄' }}</span>
                <el-button size="small" :type="showAbnormalBox ? 'danger' : 'default'" plain @click="showAbnormalBox = !showAbnormalBox">
                  <i class="el-icon-aim"></i> {{ showAbnormalBox ? '隱藏標示' : '顯示標示' }}
                </el-button>
              </div>
            </div>

            <div class="img-wrapper" @wheel.prevent="handleWheel" @mousedown="startDrag">
              <div class="img-inner" :style="panZoomStyle">
                <template v-if="secureSpriteUrl">
                  <img :src="secureSpriteUrl" class="sprite-img" :style="{ transform: `translate(-50%, ${showAbnormalBox ? '-50%' : '0%'})` }" draggable="false" />
                </template>
                <template v-else>
                  <img :src="abnormalImageUri" class="twin-img" draggable="false" />
                  <div v-if="showAbnormalBox && camera.boxPosition && !hasOverlayData" :style="getBoxStyle(camera.boxPosition)"></div>
                </template>

                <svg v-if="showAbnormalBox && hasOverlayData" class="overlay-svg" viewBox="0 0 1920 1080" preserveAspectRatio="none">
                  <rect v-for="(box, i) in camera.bboxes" :key="'r-box-'+i" 
                        :x="box[0]" :y="box[1]" :width="box[2]-box[0]" :height="box[3]-box[1]" 
                        class="event-bbox" />
                  <polygon v-for="(poly, i) in camera.polygons" :key="'r-poly-'+i" 
                           :points="formatPolygonPoints(poly)" 
                           class="event-polygon" />
                </svg>
              </div>
            </div>
          </el-card>
        </el-col>

      </el-row>
    </div>

    <div slot="footer" class="footer-container">
      <div class="alert-box">
        <div class="alert-title">
          <i class="el-icon-info"></i> 事件說明
        </div>
        <div class="alert-content">
          <strong>[{{ camera.type }}]</strong> {{ camera.description || '無詳細說明' }}
        </div>
      </div>
      <el-button v-if="secureSpriteUrl" type="success" icon="el-icon-download" @click="downloadFullSprite">
        下載完整合成圖
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  props: {
    camera: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      scale: 1,
      offset: { x: 0, y: 0 },
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      showCompareBox: false,
      showAbnormalBox: true,
      fallbackImg: 'https://via.placeholder.com/1280x720/000000/FFFFFF/?text=No+Image',
      secureSpriteUrl: '',
      imageLoading: false,
      imageBlob: null, // 快取下載下來的 Blob 供直接下載使用
    };
  },
  computed: {
    panZoomStyle() {
      return {
        width: '100%',
        height: '100%',
        transformOrigin: 'center center',
        transform: `translate(${this.offset.x}px, ${this.offset.y}px) scale(${this.scale})`,
        cursor: this.scale > 1 ? (this.isDragging ? 'grabbing' : 'grab') : 'default',
        transition: this.isDragging ? 'none' : 'transform 0.1s ease-out',
      };
    },
    compareImageUri() {
      if (this.camera.historyImages && this.camera.historyImages.length > 1) {
        return this.camera.historyImages[1].uri;
      }
      return this.fallbackImg;
    },
    abnormalImageUri() {
      if (this.camera.historyImages && this.camera.historyImages.length > 0) {
        return this.camera.historyImages[0].uri;
      }
      return this.fallbackImg;
    },
    hasOverlayData() {
      return (this.camera.bboxes && this.camera.bboxes.length > 0) || 
             (this.camera.polygons && this.camera.polygons.length > 0);
    },
  },
  watch: {
    'camera.eventRef': {
      handler(newUrl) {
        if (newUrl) {
          this.loadSecureSprite(newUrl);
        }
      },
      immediate: true,
    },
  },
  methods: {
    async loadSecureSprite(eventRef) {
      this.imageLoading = true;
      try {
        const response = await fetch('https://thsrcctv.com/events/image', {
          method: 'POST',
          headers: {
            'Authorization': localStorage.getItem('jwt'),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ event_ref: eventRef }),
        });

        if (!response.ok) throw new Error(`影像載入失敗，狀態碼: ${response.status}`);

        this.imageBlob = await response.blob();
        
        // 釋放舊的 Blob URL 記憶體
        if (this.secureSpriteUrl && this.secureSpriteUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.secureSpriteUrl);
        }

        this.secureSpriteUrl = URL.createObjectURL(this.imageBlob);
      } catch (error) {
        console.error('下載加密影像失敗:', error);
        this.$message.error('無法讀取該事件影像（未授權或連線異常）');
      } finally {
        this.imageLoading = false;
      }
    },
    formatPolygonPoints(coords) {
      if (!coords || !coords.length) return '';
      let pointsStr = '';
      for (let i = 0; i < coords.length; i += 2) {
        if (coords[i] !== undefined && coords[i+1] !== undefined) {
          pointsStr += `${coords[i]},${coords[i+1]} `;
        }
      }
      return pointsStr.trim();
    },
    changeZoom(delta) {
      this.scale = Math.min(Math.max(this.scale + delta, 1), 5);
      if (this.scale === 1) this.offset = { x: 0, y: 0 };
    },
    resetZoom() {
      this.scale = 1;
      this.offset = { x: 0, y: 0 };
    },
    handleWheel(e) {
      this.changeZoom(e.deltaY < 0 ? 0.1 : -0.1);
    },
    startDrag(e) {
      if (this.scale <= 1) return;
      this.isDragging = true;
      this.dragStart = { x: e.clientX - this.offset.x, y: e.clientY - this.offset.y };
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      let newX = e.clientX - this.dragStart.x;
      let newY = e.clientY - this.dragStart.y;
      
      if (this.$refs.leftContainerRef) {
        const container = this.$refs.leftContainerRef;
        const maxX = (container.clientWidth * (this.scale - 1)) / 2;
        const maxY = (container.clientHeight * (this.scale - 1)) / 2;
        
        const clampedX = Math.min(Math.max(newX, -maxX), maxX);
        const clampedY = Math.min(Math.max(newY, -maxY), maxY);

        if (clampedX !== newX) this.dragStart.x = e.clientX - clampedX;
        if (clampedY !== newY) this.dragStart.y = e.clientY - clampedY;
        
        newX = clampedX;
        newY = clampedY;
      }
      this.offset = { x: newX, y: newY };
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.stopDrag);
    },
    getBoxStyle(pos) {
      return {
        position: 'absolute',
        left: `${(pos.x / 1280) * 100}%`,
        top: `${(pos.y / 720) * 100}%`,
        width: '25%',   
        height: '25%',  
        border: `${2 / this.scale}px solid red`, 
        boxSizing: 'border-box',
        zIndex: 10,
        backgroundColor: 'rgba(255, 0, 0, 0.1)',
      };
    },
    downloadView(type) {
      this.$message(`準備下載 ${type === 'compare' ? '對照' : '異常'}檢視圖...`);
    },
    downloadFullSprite() {
      if (!this.imageBlob) {
        this.$message.warning('影像尚未完成下載，請稍後');
        return;
      }

      try {
        const blobUrl = window.URL.createObjectURL(this.imageBlob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${this.camera.name}_完整合成圖.jpg`;
        document.body.appendChild(link);
        link.click();
        
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        
        this.$message.success('下載成功');
      } catch (error) {
        console.error('下載失敗:', error);
        this.$message.error('下載失敗。');
      }
    },
  },
  beforeDestroy() {
    if (this.secureSpriteUrl && this.secureSpriteUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.secureSpriteUrl);
    }
  },
};
</script>

<style scoped>
::v-deep .el-dialog__body {
  padding: 0 !important;
}
::v-deep .el-dialog__header {
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
}
::v-deep .el-dialog__footer {
  padding: 15px 20px;
  border-top: 1px solid #ebeef5;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.header-title {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
}
.header-tools {
  display: flex;
  align-items: center;
  gap: 15px;
}
.tip-text {
  font-size: 13px;
  color: #909399;
  background: #f4f4f5;
  padding: 4px 10px;
  border-radius: 4px;
}

.main-container {
  background-color: #f0f2f5;
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
}
.card-title {
  font-weight: bold;
  font-size: 16px;
  color: #303133;
}
.card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.time-badge {
  background-color: #f4f4f5;
  color: #606266;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
}

.img-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background-color: #000;
  overflow: hidden;
}
.img-inner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sprite-img {
  position: absolute;
  max-width: none;
  width: 200%;
  height: 200%;
  transition: transform 0.2s ease-in-out;
}
.twin-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.overlay-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}
.event-bbox {
  fill: none;
  stroke: #ffcc00;
  stroke-width: 4px;
  stroke-dasharray: 8, 4;
}
.event-polygon {
  fill: rgba(245, 108, 108, 0.25);
  stroke: #f56c6c;
  stroke-width: 2px;
}

.footer-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.alert-box {
  flex: 1;
  text-align: left;
  background-color: #fafbfd;
  color: #8c1919;
  border: 1px solid #ebeef5;
  padding: 15px 20px;
  border-radius: 6px;
  margin-right: 20px;
}
.alert-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.alert-title i {
  font-size: 20px;
  margin-right: 8px;
}
.alert-content {
  font-size: 15px;
  margin-left: 28px;
  color: #8c1919;
}
</style>